import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";

const MONTHS = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
];

/**
 * MonthYearPicker
 *
 * Props:
 *   selectedMonth  {number}  1-12   (controlled)
 *   selectedYear   {number}         (controlled)
 *   onChange       {fn}   ({ month, year }) => void
 *   label          {string}  optional label
 *   minYear        {number}  default currentYear - 5
 *   maxYear        {number}  default currentYear
 */
const MonthYearPicker = ({
    selectedMonth,
    selectedYear,
    onChange,
    label = "Select Month",
    minYear,
    maxYear,
}) => {
    const now = new Date();
    const MIN_YEAR = minYear ?? now.getFullYear() - 5;
    const MAX_YEAR = maxYear ?? now.getFullYear();

    const [open, setOpen] = useState(false);
    const [displayYear, setDisplayYear] = useState(selectedYear ?? now.getFullYear());
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

    const btnRef = useRef(null);
    const dropdownRef = useRef(null);

    const DROPDOWN_HEIGHT = 290; // approx rendered height of the picker

    /* ── Position the portal relative to the trigger button ── */
    const reposition = useCallback(() => {
        if (!btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUpward = spaceBelow < DROPDOWN_HEIGHT + 12;

        setDropdownPos({
            left: rect.left + window.scrollX,
            top: openUpward
                ? rect.top + window.scrollY - DROPDOWN_HEIGHT - 8
                : rect.bottom + window.scrollY + 8,
        });
    }, []);

    const handleOpen = () => {
        reposition();
        setOpen((v) => !v);
    };

    /* Close on outside click */
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (
                btnRef.current && !btnRef.current.contains(e.target) &&
                dropdownRef.current && !dropdownRef.current.contains(e.target)
            ) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    /* Reposition on scroll / resize */
    useEffect(() => {
        if (!open) return;
        window.addEventListener("scroll", reposition, true);
        window.addEventListener("resize", reposition);
        return () => {
            window.removeEventListener("scroll", reposition, true);
            window.removeEventListener("resize", reposition);
        };
    }, [open, reposition]);

    /* Sync displayYear when prop changes externally */
    useEffect(() => {
        if (selectedYear) setDisplayYear(selectedYear);
    }, [selectedYear]);

    const handleMonthClick = (monthIdx) => {
        onChange({ month: monthIdx + 1, year: displayYear });
        setOpen(false);
    };

    const prevYear = () => setDisplayYear((y) => Math.max(y - 1, MIN_YEAR));
    const nextYear = () => setDisplayYear((y) => Math.min(y + 1, MAX_YEAR));

    const isFuture = (monthIdx) => {
        const m = monthIdx + 1;
        return displayYear > now.getFullYear() ||
            (displayYear === now.getFullYear() && m > now.getMonth() + 1);
    };

    const isSelected  = (monthIdx) => selectedYear === displayYear && selectedMonth === monthIdx + 1;
    const isCurrent   = (monthIdx) => now.getFullYear() === displayYear && now.getMonth() === monthIdx;

    const hasSelection = selectedMonth && selectedYear;
    const displayLabel = hasSelection
        ? `${MONTHS[selectedMonth - 1].slice(0, 3)} ${selectedYear}`
        : label;

    return (
        <>
            {/* ── Trigger button ── */}
            <button
                ref={btnRef}
                onClick={handleOpen}
                className="flex items-center gap-2 px-4 py-2 h-10 bg-white border border-emerald-200 rounded-xl shadow-sm text-sm font-semibold text-emerald-800 hover:bg-emerald-50 hover:border-emerald-400 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            >
                <CalendarDaysIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span>{displayLabel}</span>
                {hasSelection && (
                    <span
                        onClick={(e) => { e.stopPropagation(); onChange({ month: null, year: null }); }}
                        className="ml-1 p-0.5 rounded-full hover:bg-emerald-100 cursor-pointer"
                    >
                        <XMarkIcon className="w-3.5 h-3.5 text-emerald-500" />
                    </span>
                )}
            </button>

            {/* ── Portal dropdown — rendered into document.body ── */}
            {open && createPortal(
                <div
                    ref={dropdownRef}
                    style={{
                        position: "absolute",
                        top: dropdownPos.top,
                        left: dropdownPos.left,
                        zIndex: 99999,
                        width: "288px",
                        borderRadius: "16px",
                        overflow: "hidden",
                        background: "#ffffff",
                        boxShadow: "0 20px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(16,185,129,0.15)",
                    }}
                >
                    {/* Year navigation */}
                    <div className="flex items-center justify-between px-4 py-3 bg-emerald-600">
                        <button
                            onClick={prevYear}
                            disabled={displayYear <= MIN_YEAR}
                            className="p-1 rounded-lg text-white hover:bg-emerald-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeftIcon className="w-4 h-4" />
                        </button>
                        <span className="text-white font-bold text-base tracking-wide">{displayYear}</span>
                        <button
                            onClick={nextYear}
                            disabled={displayYear >= MAX_YEAR}
                            className="p-1 rounded-lg text-white hover:bg-emerald-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronRightIcon className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Month grid */}
                    <div className="grid grid-cols-3 gap-1.5 p-3">
                        {MONTHS.map((month, idx) => {
                            const future   = isFuture(idx);
                            const selected = isSelected(idx);
                            const current  = isCurrent(idx);
                            return (
                                <button
                                    key={month}
                                    onClick={() => !future && handleMonthClick(idx)}
                                    disabled={future}
                                    className={`
                                        relative py-2 rounded-xl text-sm font-medium transition-all duration-150
                                        ${future
                                            ? "text-gray-300 cursor-not-allowed"
                                            : selected
                                                ? "bg-emerald-600 text-white shadow-md scale-105"
                                                : current
                                                    ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300 hover:bg-emerald-200"
                                                    : "text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                                        }
                                    `}
                                >
                                    {current && !selected && (
                                        <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                    )}
                                    {month.slice(0, 3)}
                                </button>
                            );
                        })}
                    </div>

                    {/* Footer — This Month shortcut */}
                    <div className="px-3 pb-3">
                        <button
                            onClick={() => {
                                const m = now.getMonth() + 1;
                                const y = now.getFullYear();
                                setDisplayYear(y);
                                onChange({ month: m, year: y });
                                setOpen(false);
                            }}
                            className="w-full py-1.5 rounded-xl text-xs font-semibold text-emerald-600 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                        >
                            This Month
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default MonthYearPicker;